// src/pages/ParkingUpdate.js
import React, { useState } from "react";
import { db } from "../firebase/firebase";
import {
  updateDoc,
  doc,
  serverTimestamp,
  collection,
  getDocs,
} from "firebase/firestore";
import { useClients } from "../hooks/useClients";
import { useParkingTrips } from "../hooks/useParkingTrips";
import { exportTripsToExcel } from "../utils/exportTripsToExcel";


export default function ParkingUpdate() {
  const [batchDate, setBatchDate] = useState("");
    const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");


  // 🔹 Load clients hook
  const { clientId, clientList, handleClientSelect } = useClients();

  // 🔹 Parking trips hook (supports ALL CLIENTS + airport filter)
  const {
    rows,
    setRows,
    loading,
    filter,
    setFilter,
    hasMore,
    loadTrips,
  } = useParkingTrips({ clientId, batchDate, pageSize: 100 });

  // -------------------------------------------------------------
  // ✅ Update a field (actualParking / clientParking / driverParking)
  //    • No auto-lock
  //    • Always use r.realBatchKey for ALL CLIENTS mode
  // -------------------------------------------------------------
  async function updateParking(r, field, value) {
    const v = value === "" ? "" : Number(value);

    r[field] = v; // update local

    // Recalculate totals (actualParking NOT included)
    const clientTotal =
      (Number(r.clientFinalPrice) || 0) + (Number(r.clientParking) || 0);

    const driverTotal =
      (Number(r.driverFinalPrice) || 0) + (Number(r.driverParking) || 0);

    r.totalClient = clientTotal;
    r.totalDriver = driverTotal;

    setRows([...rows]);

    try {
      // ⭐ Correct save path for both modes
      const batchKey = r.realBatchKey || `${clientId}_${batchDate}`;

      await updateDoc(doc(db, `tripImports/${batchKey}/trips/${r.id}`), {
        [field]: v === "" ? null : Number(v),
        totalClient: clientTotal,
        totalDriver: driverTotal,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
      alert("Failed to save parking update.");
    }
  }

  // -------------------------------------------------------------
  // ⭐ USER ACTION: Mark Done (Lock the row)
  // -------------------------------------------------------------
  async function markParkingDone(r) {
    try {
      const batchKey = r.realBatchKey || `${clientId}_${batchDate}`;

      await updateDoc(doc(db, `tripImports/${batchKey}/trips/${r.id}`), {
        parkingDone: true,
        updatedAt: serverTimestamp(),
      });

      r.parkingDone = true;
      setRows([...rows]);
    } catch (e) {
      console.error(e);
      alert("Failed to mark parking as done.");
    }
  }

  // -------------------------------------------------------------
  // ⭐ USER ACTION: Unlock (Allow editing again)
  // -------------------------------------------------------------
  async function unlockParking(r) {
    try {
      const batchKey = r.realBatchKey || `${clientId}_${batchDate}`;

      await updateDoc(doc(db, `tripImports/${batchKey}/trips/${r.id}`), {
        parkingDone: false,
        updatedAt: serverTimestamp(),
      });

      r.parkingDone = false;
      setRows([...rows]);
    } catch (e) {
      console.error(e);
      alert("Failed to unlock row.");
    }
  }

// ------------------------------------------------------
// ✅ Export Parking Excel (ALL CLIENTS or single client)
async function exportParkingExcel() {
  if (!fromDate || !toDate) {
    alert("Select From + To date first.");
    return;
  }

  let allTrips = [];

  try {
    const importsSnap = await getDocs(collection(db, "tripImports"));

    for (const folder of importsSnap.docs) {
      const batchKey = folder.id;

      // --- FIXED: split folder name correctly ---
      const parts = batchKey.split("_");
      const date = parts[parts.length - 1];                 // last segment
      const cId = parts.slice(0, parts.length - 1).join("_"); // clientId full

      // Client filter
      if (clientId !== "all_clients" && cId !== clientId) continue;

      // Date range filter
      if (!(date >= fromDate && date <= toDate)) continue;

      // Load trips inside this batch
      const tripsRef = collection(db, `tripImports/${batchKey}/trips`);
      const tripsSnap = await getDocs(tripsRef);

      tripsSnap.forEach((t) =>
        allTrips.push({
          ...t.data(),
          id: t.id,
          realBatchKey: batchKey,
          clientKey: cId,
          batchDate: date,
        })
      );
    }

    if (allTrips.length === 0) {
      alert("⚠ No trips found for selected date range.");
      return;
    }

    await exportTripsToExcel(
      allTrips,
      `parking_${clientId}_${fromDate}_${toDate}.xlsx`
    );

    alert("✅ Export completed!");
  } catch (error) {
    console.error(error);
    alert("Failed to export Excel.");
  }
}

  // UI
  // -------------------------------------------------------------
  return (
    <div style={{ padding: 20 }}>
      <h2>🅿️ Parking Update</h2>

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
        <label>Client:</label>
        <select value={clientId} onChange={handleClientSelect}>
          <option value="">Select client</option>
          <option value="all_clients">🌍 All Clients</option>
          {clientList.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
          <option value="add_new">➕ Add New Client</option>
        </select>

        <label style={{ marginLeft: 12 }}>Date:</label>
        <input type="date" value={batchDate} onChange={(e) => setBatchDate(e.target.value)} />

        <label style={{ marginLeft: 12 }}>Filter:</label>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="pending">⏳ Pending</option>
          <option value="done">✅ Done</option>
          <option value="all">📋 All</option>
        </select>

        <button
          onClick={() => loadTrips(true)}
          style={{
            padding: "6px 12px",
            background: "#0066cc",
            color: "#fff",
            borderRadius: 6,
            fontWeight: 600,
          }}
        >
          Load
        </button>
      </div>
      {/* Export by date range */}
<div
  style={{
    marginBottom: 15,
    display: "flex",
    gap: 10,
    alignItems: "center",
  }}
>
  <label>From:</label>
  <input
    type="date"
    value={fromDate}
    onChange={(e) => setFromDate(e.target.value)}
  />

  <label>To:</label>
  <input
    type="date"
    value={toDate}
    onChange={(e) => setToDate(e.target.value)}
  />

  <button
    onClick={exportParkingExcel}
    style={{
      padding: "6px 12px",
      background: "green",
      color: "#fff",
      borderRadius: 6,
      fontWeight: 600,
    }}
  >
    ⬇ Export Parking (Excel)
  </button>
</div>


      {loading && <p>⏳ Loading trips…</p>}

      {rows.length > 0 && (
        <>
          <p style={{ marginBottom: 10 }}>
            Showing <b>{rows.length}</b> trips{" "}
            {filter !== "all" && (
              <span>({filter === "pending" ? "Pending only" : "Completed only"})</span>
            )}
          </p>

          <table border="1" cellPadding="6" style={{ background: "#fff", width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ background: "#f8f8f8" }}>
              <tr>
                <th>#</th>
                <th>Client</th>
                <th>Client Trip ID</th>
                <th>CLL Trip ID</th>
                <th>Passenger</th>
                <th>Phone</th>
                <th>No. of Pax</th>
                <th>Flight No</th>
                <th>Pickup</th>
                <th>Drop</th>
                <th>Car Type</th>
                <th>Driver</th>
                <th>Actual Parking</th>
                <th>Client Price</th>
                <th>Driver Price</th>
                <th>Client Parking</th>
                <th>Driver Parking</th>
                <th>Total Client</th>
                <th>Total Driver</th>
                <th>Comments</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} style={{ background: r.parkingDone ? "#e8fbe8" : "#fff9db" }}>
                  <td>{i + 1}</td>
                  <td>{r.clientKey}</td>
                  <td>{r.reservationId}</td>
                  <td>{r.cllTripId}</td>
                  <td>{r.passengerName}</td>
                  <td>{r.passengerPhone}</td>
                  <td>{r.noOfPassengers}</td>
                  <td>{r.flightNumber}</td>
                  <td>{r.pickup}</td>
                  <td>{r.drop}</td>
                  <td>{r.carType}</td>
                  <td>{r.driver}</td>

                  {/* Actual Parking */}
                  <td>
                    <input
                      type="number"
                      defaultValue={r.actualParking || ""}
                      onBlur={(e) => updateParking(r, "actualParking", e.target.value)}
                      style={{ width: 90 }}
                      disabled={r.parkingDone}
                    />
                  </td>

                  {/* Read-only prices */}
                  <td>{r.clientFinalPrice}</td>
                  <td>{r.driverFinalPrice}</td>

                  {/* Client Parking */}
                  <td>
                    <input
                      type="number"
                      defaultValue={r.clientParking ?? ""}
                      onBlur={(e) => updateParking(r, "clientParking", e.target.value)}
                      style={{ width: 90 }}
                      disabled={r.parkingDone}
                    />
                  </td>

                  {/* Driver Parking */}
                  <td>
                    <input
                      type="number"
                      defaultValue={r.driverParking ?? ""}
                      onBlur={(e) => updateParking(r, "driverParking", e.target.value)}
                      style={{ width: 90 }}
                      disabled={r.parkingDone}
                    />
                  </td>

                  <td>{r.totalClient}</td>
                  <td>{r.totalDriver}</td>

                  <td>{r.comment}</td>

                  {/* Lock / Unlock */}
                  <td>
                    {r.parkingDone ? (
                      <button
                        style={{
                          padding: "2px 8px",
                          background: "#ffcc00",
                          border: "none",
                          borderRadius: 4,
                          cursor: "pointer",
                        }}
                        onClick={() => unlockParking(r)}
                      >
                        ✏️ Unlock
                      </button>
                    ) : (
                      <button
                        style={{
                          padding: "2px 8px",
                          background: "#00b894",
                          color: "#fff",
                          border: "none",
                          borderRadius: 4,
                          cursor: "pointer",
                        }}
                        onClick={() => markParkingDone(r)}
                      >
                        ✔ Mark Done
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {hasMore && (
            <button
              onClick={() => loadTrips(false)}
              style={{
                marginTop: 12,
                padding: "8px 14px",
                background: "#008CBA",
                color: "#fff",
                borderRadius: 6,
                border: "none",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Load More Trips ⬇️
            </button>
          )}
        </>
      )}
    </div>
  );
}
